import { z } from 'zod';

import type { CreateStateRequestViewModel } from '@/shared/api/generated';

/**
 * Schema do formulário de Estado (criar/editar). Validado por Zod (React Hook
 * Form + resolver).
 *
 * NÃO derivado de `createStateRequestViewModelSchema` (gerado pelo Kubb): aquele
 * schema é exportado com o cast `as unknown as ToZod<...>`, o que torna a shape do
 * `ZodObject` uma ficção de tipo — qualquer `.pick()` / `.extend()` degrada o
 * `z.infer` para `never`. Por isso é definido aqui, endurecendo `code`, `name` e
 * `abbreviation` como obrigatórios e `countryId` como número positivo (o contrato
 * gerado os tem como opcionais/nullish), refletindo os campos `Código*`/`Nome*`/
 * `Sigla*`/`País*` do protótipo `design/location.pen`.
 */
export const stateFormSchema = z.object({
  code: z.string().trim().min(1),
  name: z.string().trim().min(1),
  abbreviation: z.string().trim().min(1),
  countryId: z.number().int().positive(),
});

/**
 * Valores do formulário de Estado (create/edit).
 *
 * O tipo intersecta com uma asserção nula (no-op) que só permanece `unknown`
 * enquanto `code | name | abbreviation | countryId` continuarem existindo em
 * `CreateStateRequestViewModel` — se um for renomeado/removido no contrato gerado
 * pelo Kubb, a asserção vira `never` e o `yarn type-check` quebra aqui.
 */
export type StateFormValues = z.infer<typeof stateFormSchema> &
  ('code' | 'name' | 'abbreviation' | 'countryId' extends keyof CreateStateRequestViewModel
    ? unknown
    : never);
